import React, { useState, useMemo } from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Drawer,
    useMediaQuery,
    useTheme,
    Divider,
    Button,
    Menu,
    MenuItem
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Logout } from '@mui/icons-material';
import HomeIcon from '../../../assets/images/home_icon.svg';
import BarChartIcon from '../../../assets/images/bar_chart_icon.svg';
import BillingIcon from '../../../assets/images/billing_icon.svg';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import EditIcon from '../../../assets/images/edit_icon.svg';
import QuestionIcon from '../../../assets/images/question_icon.svg';
import FindIcon from '../../../assets/images/find_icon.svg';
import { ROUTES } from '../../../constants';
import { useElectricPage } from '../../../helpers/isElectricPage';
import { getContracts } from '../../../api/ElectricApi';
import LogoPortal from '../../../components/common/LogoPortal';
import FeedbackIcon from '../../../assets/images/feedback_icon.svg';
import FeedbackIconMobile from '../../../assets/images/feedback_icon_mobile.svg';

const Sidebar = ({ mobileOpen, onMobileClose, onLogout }) => {
    const history = useHistory();
    const location = useLocation();
    const isElectricPage = useElectricPage();
    const profile = useSelector((state) => state.user.profile);
    const isBusinessAccount = useMemo(() => profile?.is_business_account === true, [profile]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isNarrowMobile = useMediaQuery('(max-width:375px)');
    const [anchorElProcedure, setAnchorElProcedure] = useState(null);
    const [anchorElDr, setAnchorElDr] = useState(null);
    const [isHoveredFeedback, setIsHoveredFeedback] = useState(false);
    const { data: contractsResponse } = useQuery(['getContracts'], () => getContracts(1, 1000), {
        enabled: isElectricPage
    });
    const hasDrContract = useMemo(() => {
        const list = contractsResponse?.data ?? [];
        return list.some((c) => c.drValidFlag === true);
    }, [contractsResponse]);
    const feedbackUrl = profile?.is_business_account
        ? 'https://shirokumapower.com/portal-advice-B'
        : 'https://shirokumapower.com/portal-advice-C';
    const menuItems = useMemo(() => {
        const items = [
            { id: 'home', label: 'ホーム', icon: HomeIcon, path: ROUTES.ELECTRIC.BASE },
            { id: 'usage', label: '電気料金・使用量', icon: BarChartIcon, path: ROUTES.ELECTRIC.USAGE },
            { id: 'billing', label: 'ご利用明細・請求書', icon: BillingIcon, path: ROUTES.ELECTRIC.BILLING }
        ];
        if (hasDrContract) {
            items.push({
                id: 'dr-schedule',
                label: '節電DR要請',
                icon: EnergySavingsLeafIcon,
                path: ROUTES.ELECTRIC.DR_SCHEDULE,
                iconSize: 34
            });
        }
        items.push(
            { id: 'procedures', label: '各種手続き', icon: EditIcon, path: ROUTES.ELECTRIC.PROCEDURE },
            {
                id: 'faq',
                label: 'よくある質問',
                icon: QuestionIcon,
                path: ROUTES.ELECTRIC.FAQ
            },
            {
                id: 'info',
                label: 'お役立ち情報',
                icon: FindIcon,
                path: null,
                iconSize: 28,
                onClick: () => {
                    console.log(isBusinessAccount);
                    window.open(
                        isBusinessAccount ? 'https://contents.shirokumapower.com/blog' : 'https://contents.shirokumapower.com/blog-2c',
                        '_blank'
                    );
                }
            }
        );
        return items;
    }, [hasDrContract, isBusinessAccount]);

    const BUSINESS_INQUIRY_URL = 'https://shirokumapower.com/Inq-cstmr-high';

    const contactAction = () => {
        if (isBusinessAccount) {
            window.location.assign(BUSINESS_INQUIRY_URL);
        } else {
            history.push(ROUTES.ELECTRIC.INQUIRY);
        }
        if (isMobile) onMobileClose();
    };

    const handleProcedureOpen = (event) => setAnchorElProcedure(event.currentTarget);
    const handleDrOpen = (event) => setAnchorElDr(event.currentTarget);
    const handleMenusClose = () => {
        setAnchorElProcedure(null);
        setAnchorElDr(null);
    };

    const handleProcedureSelect = (tab) => {
        history.push(`${ROUTES.ELECTRIC.PROCEDURE}?tab=${tab}`);
        handleMenusClose();
        if (isMobile) onMobileClose();
    };

    const handleDrSelect = (tab) => {
        history.push(`${ROUTES.ELECTRIC.DR_SCHEDULE}?tab=${tab}`);
        handleMenusClose();
        if (isMobile) onMobileClose();
    };

    const isActive = (path) => {
        if (!path) return false;
        return path === ROUTES.ELECTRIC.BASE
            ? location.pathname === path || location.pathname === path + '/'
            : location.pathname.startsWith(path);
    };

    if (!isElectricPage) return null;

    const menuPaperProps = {
        sx: {
            borderRadius: '16px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            overflow: 'visible',
            '& .MuiList-root': { padding: '8px 0' }
        }
    };
    const sidebarContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {!isMobile && <LogoPortal />}

                <Box
                    sx={{
                        overflowY: 'auto',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        py: isMobile ? (isNarrowMobile ? 0.75 : 1) : 2
                    }}
                >
                    <List>
                        {menuItems.map((item) => (
                            <React.Fragment key={item.id}>
                                <ListItem disablePadding sx={{ px: '20px', display: 'flex', justifyContent: 'center' }}>
                                    <ListItemButton
                                        onClick={(e) => {
                                            if (item.id === 'procedures') handleProcedureOpen(e);
                                            else if (item.id === 'dr-schedule') handleDrOpen(e);
                                            else if (item.path) history.push(item.path);
                                            else item.onClick?.();
                                            if (isMobile && item.id !== 'procedures' && item.id !== 'dr-schedule') onMobileClose();
                                        }}
                                        sx={{
                                            width: '210px!important',
                                            height: isNarrowMobile && isMobile ? 46 : 50,
                                            px: isNarrowMobile && isMobile ? 2.5 : 3,
                                            borderRadius: '16px',
                                            transition: 'all 0.2s ease',
                                            ...(isActive(item.path) && {
                                                bgcolor: '#fff',
                                                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 6px 2px'
                                            })
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: isMobile ? 0 : 48, color: '#333' }}>
                                            {typeof item.icon === 'string' ? (
                                                <img
                                                    src={item.icon}
                                                    alt=""
                                                    style={{ width: item.iconSize || 26, height: item.iconSize || 26 }}
                                                />
                                            ) : (
                                                <item.icon sx={{ fontSize: item.iconSize || 26 }} />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: '14px', sm: '12px' },
                                                        fontWeight: isActive(item.path) ? 'bold' : 600,
                                                        color: '#333',
                                                        whiteSpace: { xs: 'nowrap', md: 'pre-line' },
                                                        ml: { xs: 2, sm: 0 }
                                                    }}
                                                >
                                                    {isMobile ? item.label.replace(/\n/g, ' ') : item.label}
                                                </Typography>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                                {item.id === 'dr-schedule' && (
                                    <Menu
                                        anchorEl={anchorElDr}
                                        open={Boolean(anchorElDr)}
                                        onClose={handleMenusClose}
                                        disableScrollLock
                                        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                                        PaperProps={menuPaperProps}
                                    >
                                        <MenuItem
                                            onClick={() => handleDrSelect('schedule')}
                                            sx={{ py: 1.5, px: 3, fontSize: '12px', fontWeight: 600 }}
                                        >
                                            節電予定
                                        </MenuItem>
                                        <Divider sx={{ my: 0.5 }} />
                                        <MenuItem
                                            onClick={() => handleDrSelect('history')}
                                            sx={{ py: 1.5, px: 3, fontSize: '12px', fontWeight: 600 }}
                                        >
                                            節電履歴
                                        </MenuItem>
                                    </Menu>
                                )}
                                {item.id === 'procedures' && (
                                    <Menu
                                        anchorEl={anchorElProcedure}
                                        open={Boolean(anchorElProcedure)}
                                        onClose={handleMenusClose}
                                        disableScrollLock
                                        anchorOrigin={{
                                            vertical: 'center',
                                            horizontal: 'right'
                                        }}
                                        transformOrigin={{
                                            vertical: 'center',
                                            horizontal: 'left'
                                        }}
                                        PaperProps={{
                                            sx: {
                                                borderRadius: '16px',
                                                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                                                overflow: 'visible',
                                                '& .MuiList-root': {
                                                    padding: '8px 0'
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem
                                            onClick={() => handleProcedureSelect('member')}
                                            sx={{ py: 1.5, px: 3, fontSize: '12px', fontWeight: 600, color: '#333' }}
                                        >
                                            会員情報の確認・変更
                                        </MenuItem>
                                        <Divider sx={{ my: 0.5 }} />
                                        <MenuItem
                                            onClick={() => handleProcedureSelect('contract')}
                                            sx={{ py: 1.5, px: 3, fontSize: '12px', fontWeight: 600, color: '#333' }}
                                        >
                                            契約確認
                                        </MenuItem>
                                        {/* {!isBusinessAccount && ( */}
                                        {/* <> */}
                                        <Divider sx={{ my: 0.5 }} />
                                        <MenuItem
                                            onClick={() => handleProcedureSelect('document')}
                                            sx={{ py: 1.5, px: 3, fontSize: '12px', fontWeight: 600, color: '#333' }}
                                        >
                                            資料確認
                                        </MenuItem>
                                        {/* </>
                                    )} */}
                                    </Menu>
                                )}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        my: isMobile ? (isNarrowMobile ? 0.5 : 1) : 2
                                    }}
                                >
                                    <Divider sx={{ width: '70%', borderColor: '#ccc', borderBottomWidth: 1.2 }} />
                                </Box>
                            </React.Fragment>
                        ))}
                    </List>
                    <Box sx={{ px: 3, mb: isMobile ? 2 : 3 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={contactAction}
                            sx={{
                                bgcolor: '#f2f2f2',
                                color: '#333',
                                boxShadow: 'none',
                                py: 1.8,
                                fontSize: '12px',
                                fontWeight: 'bold',
                                borderRadius: '4px',
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#e0e0e0', boxShadow: 'none' }
                            }}
                        >
                            お問い合わせ
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            mt: isMobile ? 'auto' : 1,
                            pb: isMobile ? 4 : 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mb: isMobile ? (isNarrowMobile ? '12px' : 4) : 0
                            }}
                        >
                            <a
                                href={feedbackUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-block',
                                    lineHeight: 0,
                                    width: 'fit-content',
                                    cursor: 'pointer'
                                }}
                            >
                                <img
                                    src={isMobile || isHoveredFeedback ? FeedbackIconMobile : FeedbackIcon}
                                    style={{
                                        maxWidth: isMobile ? (isNarrowMobile ? 50 : 70) : 60,
                                        width: '100%',
                                        display: 'block'
                                    }}
                                    alt="feedback"
                                    onMouseEnter={() => !isMobile && setIsHoveredFeedback(true)}
                                    onMouseLeave={() => !isMobile && setIsHoveredFeedback(false)}
                                />
                            </a>
                        </Box>

                        {isMobile && (
                            <Button
                                fullWidth
                                startIcon={<Logout />}
                                onClick={onLogout}
                                sx={{
                                    color: '#000',
                                    py: 1.5,
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    px: 2
                                }}
                            >
                                ログアウト
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={onMobileClose}
                ModalProps={{
                    keepMounted: true
                }}
                sx={{
                    zIndex: 1100,
                    '& .MuiDrawer-paper': {
                        width: '70%',
                        maxWidth: 320,
                        boxSizing: 'border-box',
                        marginTop: '64px',
                        height: 'calc(100% - 64px)',
                        boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.1)',
                        borderLeft: 'none'
                    },
                    '& .MuiBackdrop-root': {
                        marginTop: '64px'
                    }
                }}
            >
                {sidebarContent}
            </Drawer>
        );
    }

    return (
        <Box
            sx={{
                width: 250,
                bgcolor: '#fff',
                height: '100vh',
                position: 'fixed',
                left: 0,
                top: 0,
                overflowY: 'auto',
                zIndex: (theme) => theme.zIndex.drawer + 4,
                boxShadow: '4px 0 16px rgba(0, 0, 0, 0.1)'
            }}
        >
            {sidebarContent}
        </Box>
    );
};

export default Sidebar;
